import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import { useSEO, SEO_PAGES } from "../hooks/useSEO";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput, { validatePhone, formatPhone } from "../components/PhoneInput";

const C = {
  saffron:"#E8922A", saffronL:"#FDF0D5", saffronD:"#B86A10",
  sage:"#4A7C59", sageL:"#EAF4EE", sageD:"#2D5A3D",
  terra:"#B85C38",
  cream:"#FDFAF3", creamD:"#F5EDD8",
  turmeric:"#F7C948",
  ink:"#2C1810", inkMid:"#5C3D2E", inkLight:"#9A7B60",
};

const Lotus = ({ size=40, color=C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    {[0,30,60,-30,-60,90].map((r,i) => (
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i>3?0.45:i>1?0.7:0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
    <rect x="38" y="58" width="4" height="10" rx="2" fill={C.sage} opacity="0.55"/>
  </svg>
);

const STATUS_STYLES = {
  Pending:   { bg:"#FEF3C7", color:"#92400E", dot:"#F59E0B", label:"Pending"   },
  Replied:   { bg:"#DBEAFE", color:"#1E40AF", dot:"#3B82F6", label:"Replied"   },
  Completed: { bg:C.sageL,   color:C.sageD,   dot:C.sage,   label:"Completed" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg:C.creamD, color:C.inkLight, dot:C.inkLight, label:status };
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:6,
      padding:"4px 14px", borderRadius:20, fontSize:12, fontWeight:600,
      background:s.bg, color:s.color, fontFamily:"'Inter',sans-serif" }}>
      <span style={{ width:6, height:6, borderRadius:"50%",
        background:s.dot, display:"inline-block" }}/>
      {s.label}
    </span>
  );
}

function Timeline({ request }) {
  const steps = [
    { label:"Submitted", done:true, date:request.createdAt },
    { label:"Under Review", done:request.status !== "Pending" },
    { label:"Replied", done:request.status === "Replied" || request.status === "Completed" },
    { label:"Completed", done:request.status === "Completed" },
  ];
  return (
    <div style={{ display:"flex", gap:0, marginTop:16, overflowX:"auto" }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", flex:1, minWidth:80 }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flex:1 }}>
            <motion.div initial={{ scale:0 }} animate={{ scale:1 }}
              transition={{ delay:i*0.1, type:"spring" }}
              style={{ width:28, height:28, borderRadius:"50%",
                background: s.done
                  ? `linear-gradient(135deg,${C.saffron},${C.terra})`
                  : C.creamD,
                display:"flex", alignItems:"center", justifyContent:"center",
                border: s.done ? "none" : `2px solid ${C.creamD}` }}>
              {s.done
                ? <svg width="12" height="12" viewBox="0 0 10 10"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" fill="none"/></svg>
                : <div style={{ width:8, height:8, borderRadius:"50%", background:"#ccc" }}/>
              }
            </motion.div>
            <span style={{ fontSize:10, color:s.done?C.saffronD:C.inkLight,
              fontFamily:"'Inter',sans-serif", fontWeight:s.done?600:400,
              textAlign:"center", whiteSpace:"nowrap" }}>{s.label}</span>
            {s.date && (
              <span style={{ fontSize:9, color:C.inkLight, fontFamily:"'Inter',sans-serif" }}>
                {new Date(s.date).toLocaleDateString()}
              </span>
            )}
          </div>
          {i < steps.length-1 && (
            <div style={{ flex:1, height:2, background:
              steps[i+1].done ? `linear-gradient(90deg,${C.saffron},${C.terra})` : C.creamD,
              margin:"0 4px", marginBottom:20 }}/>
          )}
        </div>
      ))}
    </div>
  );
}

export default function StatusPage() {
  useSEO(SEO_PAGES.status);
  const [params] = useSearchParams();

  // FIXED: use PhoneInput's value shape {countryCode, number} instead of a
  // plain digits-only string. The old input stripped all non-digits, so the
  // country code was lost. The backend stores "+91 9876543210" (from formatPhone
  // in RequestPage), but the status lookup was sending just "9876543210" →
  // exact-match in DB always failed → "No records found".
  const [phone, setPhone] = useState(() => {
    const raw = params.get("phone") || "";
    // If redirected from RequestPage, raw is already "+91 9876543210"
    const match = raw.match(/^(\+\d{1,4})\s*(\d+)$/);
    if (match) return { countryCode: match[1], number: match[2] };
    return { countryCode: "+91", number: raw.replace(/\D/g, "") };
  });

  const [phoneError, setPhoneError] = useState("");
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const load = async (phoneVal) => {
    const err = validatePhone(phoneVal, true);
    if (err) { setPhoneError(err); return; }
    setPhoneError("");
    setLoading(true); setSearched(true);
    try {
      // Send full formatted phone e.g. "+91 9876543210" — matches what's stored in DB
      const formatted = formatPhone(phoneVal);
      const res = await api.get(`/requests/status/${encodeURIComponent(formatted)}`);
      setData(res.data || []);
    } finally { setLoading(false); }
  };

  // Auto-search if phone was pre-filled from URL (redirect after booking)
  useEffect(() => {
    if (phone.number) load(phone);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ background:C.cream, minHeight:"100vh", paddingTop:72 }}>

      {/* HERO */}
      <section style={{
        padding:"60px 24px 52px",
        background:`linear-gradient(145deg,${C.cream} 0%,${C.creamD} 45%,${C.saffronL} 100%)`,
        textAlign:"center"
      }}>
        <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.55 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6,
            marginBottom:12, background:C.saffronL,
            border:`1px solid ${C.saffron}40`, borderRadius:20, padding:"4px 16px" }}>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.13em",
              color:C.saffronD, textTransform:"uppercase",
              fontFamily:"'Inter',sans-serif" }}>Track Your Visit</span>
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(28px,4.5vw,50px)", fontWeight:800,
            color:C.ink, marginBottom:12 }}>Appointment Status</h1>
          <p style={{ fontSize:16, color:C.inkMid, fontFamily:"'Merriweather',serif",
            maxWidth:500, margin:"0 auto" }}>
            Enter your phone number to view the latest status of your appointment or enquiry.
          </p>
        </motion.div>
      </section>

      {/* SEARCH BOX */}
      <section style={{ padding:"40px 24px" }}>
        <div style={{ maxWidth:540, margin:"0 auto" }}>
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
            transition={{ delay:0.2 }}
            style={{ background:"#fff", borderRadius:22, padding:"28px 28px",
              boxShadow:"0 8px 40px rgba(0,0,0,0.075)",
              border:`1px solid ${C.creamD}` }}>

            {/* FIXED: replaced plain <input maxLength={10}> with full PhoneInput
                so country code is captured and sent to match the DB format */}
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <PhoneInput
                label="Your Phone Number"
                value={phone}
                onChange={(v) => { setPhone(v); setPhoneError(""); }}
                error={phoneError}
                required
              />
              <motion.button
                whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                onClick={() => load(phone)} disabled={loading}
                style={{ padding:"12px 22px", borderRadius:12, border:"none",
                  background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                  color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer",
                  fontFamily:"'Inter',sans-serif",
                  boxShadow:`0 4px 16px rgba(232,146,42,0.38)`,
                  opacity:loading?0.7:1 }}>
                {loading ? "Checking…" : "Check Status"}
              </motion.button>
            </div>
          </motion.div>

          {/* RESULTS */}
          <AnimatePresence>
            {searched && !loading && data.length===0 && (
              <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                exit={{ opacity:0 }}
                style={{ textAlign:"center", padding:"32px 0" }}>
                <Lotus size={44} color={C.inkLight}/>
                <p style={{ color:C.inkLight, marginTop:12, fontSize:15,
                  fontFamily:"'Merriweather',serif" }}>
                  No records found for this number.
                </p>
                <p style={{ color:C.inkLight, fontSize:13, marginTop:6,
                  fontFamily:"'Inter',sans-serif" }}>
                  Please check the number or{" "}
                  <a href="/request" style={{ color:C.saffron, textDecoration:"none" }}>
                    book a new appointment
                  </a>.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ marginTop:20 }}>
            <AnimatePresence>
              {data.map((r, idx) => (
                <motion.div key={r._id}
                  initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
                  exit={{ opacity:0 }} transition={{ delay:idx*0.08 }}
                  style={{ background:"#fff", borderRadius:20,
                    padding:"22px 24px", marginBottom:16,
                    boxShadow:"0 4px 20px rgba(0,0,0,0.065)",
                    border:`1px solid ${C.creamD}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"flex-start", marginBottom:8 }}>
                    <div>
                      <p style={{ fontFamily:"'Playfair Display',serif",
                        fontWeight:700, color:C.ink, fontSize:17 }}>{r.type}</p>
                      <p style={{ fontSize:12, color:C.inkLight,
                        fontFamily:"'Inter',sans-serif", marginTop:2 }}>
                        {new Date(r.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <StatusBadge status={r.status}/>
                  </div>

                  {r.type==="Appointment" && r.appointmentDate && (
                    <div style={{ display:"inline-flex", alignItems:"center", gap:6,
                      background:C.sageL, borderRadius:8, padding:"5px 12px",
                      marginBottom:10 }}>
                      <span style={{ fontSize:12, color:C.sageD,
                        fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                        📅 {new Date(r.appointmentDate).toDateString()}
                      </span>
                    </div>
                  )}

                  {r.message && (
                    <p style={{ fontSize:13, color:C.inkMid, lineHeight:1.6,
                      fontFamily:"'Merriweather',serif", marginBottom:12 }}>{r.message}</p>
                  )}

                  <Timeline request={r}/>

                  {r.adminReply && (
                    <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }}
                      style={{ background:C.sageL, borderRadius:10,
                        padding:"10px 14px", marginTop:14,
                        border:`1px solid ${C.sage}30` }}>
                      <p style={{ fontSize:12, fontWeight:600, color:C.sageD,
                        fontFamily:"'Inter',sans-serif", marginBottom:4 }}>💬 Clinic Reply</p>
                      <p style={{ fontSize:13, color:C.inkMid,
                        fontFamily:"'Merriweather',serif" }}>{r.adminReply}</p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {searched && (
            <div style={{ textAlign:"center", marginTop:16 }}>
              <motion.a href="/request"
                whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}
                style={{ display:"inline-flex", alignItems:"center", gap:8,
                  padding:"11px 26px", borderRadius:50, fontSize:14, fontWeight:600,
                  textDecoration:"none", fontFamily:"'Inter',sans-serif",
                  border:`2px solid ${C.saffron}`, color:C.saffron,
                  background:"transparent" }}>
                📅 Book New Appointment
              </motion.a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
